import {
    FileText,
    X,
    User,
    Mail,
    Clock,
    Calendar,
    FileCheck,
    Loader2,
} from "lucide-react";

interface Props {
    modalOpen: boolean;
    selectedRequest: any;
    closeModal: () => void;
    getStatusBadge: (status: string) => React.ReactNode;
    formatDisplayDate: (date: string) => string;
    formatDisplayDateTime: (date: string) => string;
    user: any;
    showRejectReason: boolean;
    setShowRejectReason: React.Dispatch<React.SetStateAction<boolean>>;
    rejectReason: string;
    setRejectReason: React.Dispatch<React.SetStateAction<string>>;
    approving: boolean;
    rejecting: boolean;
    openingReject: boolean;
    isApproveDisabled: boolean;
    isRejectDisabled: boolean;
    handleApproveRequest: (request: any) => void;
    handleArchival: (
        request: any,
        action: "Approve" | "Reject"
    ) => void;
    setOpeningReject: React.Dispatch<React.SetStateAction<boolean>>;
}

const ArchivalRequestModal: React.FC<Props> = ({
    modalOpen,
    selectedRequest,
    closeModal,
    getStatusBadge,
    formatDisplayDate,
    formatDisplayDateTime,
    user,
    showRejectReason,
    setShowRejectReason,
    rejectReason,
    setRejectReason,
    approving,
    rejecting,
    openingReject,
    isApproveDisabled,
    isRejectDisabled,
    handleApproveRequest,
    handleArchival,
    setOpeningReject,
}) => {

    if (!modalOpen || !selectedRequest) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6" />
                        <h2 className="text-xl font-bold">Archival Request Details</h2>
                    </div>
                    <button
                        className="text-white/80 hover:text-white transition-colors
            cursor-pointer"
                        onClick={closeModal}
                        aria-label="Close"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <div className="p-2 space-y-2">
                    {/* Request Information */}
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            Request Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Request ID
                                </p>
                                <div className="text-xs font-semibold text-gray-500">
                                    {selectedRequest.requestId}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    CNR Number
                                </p>
                                <div className="text-xs font-semibold text-gray-500">
                                    {selectedRequest.cnr}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Status
                                </p>
                                <div>{getStatusBadge(selectedRequest.status)}</div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Reason
                                </p>
                                <div className="text-xs text-gray-500">
                                    {selectedRequest.reason || "No reason provided"}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* User Information */}
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            User Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Username
                                </p>
                                <div className="text-xs font-semibold text-gray-500">
                                    {selectedRequest.userId?.username}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Full Name
                                </p>
                                <div className="text-xs font-semibold text-gray-500">
                                    {selectedRequest.userId?.firstName}{" "}
                                    {selectedRequest.userId?.lastName}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    Email Address
                                </label>
                                <div className="text-xs text-gray-500">
                                    {selectedRequest.userId?.email}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-700">
                                    Role
                                </p>
                                <div className="text-xs font-semibold text-gray-500">
                                    {selectedRequest.userId?.role}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline Information */}
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Timeline
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Request Date
                                </label>
                                <div className="text-xs font-medium text-gray-500">
                                    {formatDisplayDate(selectedRequest.createdAt)}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                    <FileCheck className="w-3 h-3" />
                                    Action Date
                                </label>
                                <div className="text-xs font-medium text-gray-500">
                                    {selectedRequest.actionDateTime
                                        ? formatDisplayDateTime(selectedRequest.actionDateTime)
                                        : "Not yet actioned"}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {user?.role === "Controller" && (
                        <div className="border-t pt-6">
                            {showRejectReason ? (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-gray-700">
                                            Reason for Rejection
                                        </p>

                                        <textarea
                                            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-700 focus:border-transparent transition-all"
                                            rows={4}
                                            placeholder="Please provide a detailed reason for rejecting this request..."
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                        />
                                    </div>

                                    <div className="flex gap-3 justify-end">
                                        <button
                                            className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-all"
                                            onClick={() => {
                                                setShowRejectReason(false);
                                                setRejectReason("");
                                            }}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            className="flex items-center justify-center gap-2 px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                                            disabled={!rejectReason.trim() || rejecting}
                                            onClick={() => {
                                                handleArchival(
                                                    { ...selectedRequest, reason: rejectReason },
                                                    "Reject",
                                                );

                                                setShowRejectReason(false);
                                                setRejectReason("");
                                            }}
                                        >
                                            {rejecting && (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            )}

                                            {rejecting ? "Rejecting..." : "Submit Rejection"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                    <button
                                        className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-8 py-3 rounded-lg font-semibold shadow-md transition-all
            ${isApproveDisabled
                                                ? "opacity-50 cursor-not-allowed bg-green-600"
                                                : "bg-green-600 hover:bg-green-700 hover:shadow-lg"
                                            }`}
                                        onClick={() => handleApproveRequest(selectedRequest)}
                                        disabled={isApproveDisabled}
                                    >
                                        {approving && (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        )}

                                        {approving ? "Approving..." : "Approve Request"}
                                    </button>

                                    <button
                                        className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-8 py-3 rounded-lg font-semibold shadow-md transition-all
            ${isRejectDisabled
                                                ? "opacity-50 cursor-not-allowed bg-red-600"
                                                : "bg-red-600 hover:bg-red-700 hover:shadow-lg"
                                            }`}
                                        onClick={() => {
                                            setOpeningReject(true);

                                            setTimeout(() => {
                                                setShowRejectReason(true);
                                                setOpeningReject(false);
                                            }, 300);
                                        }}
                                        disabled={isRejectDisabled}
                                    >
                                        {openingReject && (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        )}

                                        {openingReject ? "Rejecting..." : "Reject Request"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArchivalRequestModal;